/**
 * useContextualNotams (#8) — rewritten under #54 against the two-axis store.
 *
 * Ownership: module-level singleton loop (position provider + forced-
 * refresh timer + AbortController + backoff schedule + fail counters). All
 * callers share one loop; start()/stop() are idempotent (#47 R3-F15).
 *
 * Position source (#441 Phase 1): the loop no longer calls
 * navigator.geolocation itself — it arms whatever `PositionProvider` is
 * active. GeolocationPositionProvider is the default and reproduces the
 * previous behaviour exactly; a simulated provider drives this same loop.
 *
 * Axis separation (#54): _onError writes ONLY to gpsState; _callApi writes
 * ONLY to apiState. The two never cross-clobber, so the collision root
 * cause behind the #53 R2 findings is gone.
 *
 * Refresh cadence (per #8):
 *   - geolocation.watchPosition fires the next call when the position has
 *     moved > minDistanceM since the last call, OR
 *   - forcedRefreshMs elapsed since the last call,
 *   whichever fires first.
 *
 * Permission handling:
 *   - PERMISSION_DENIED from watchPosition: composable calls
 *     setRuntimePermissionDenied(). Since #449 that no longer drives
 *     BottomNav visibility — it latches the singleton so the loop stops
 *     re-arming and the view shows the denied placeholder. gpsState is set
 *     to 'unknown' (no useful signal to send).
 *   - POSITION_UNAVAILABLE / TIMEOUT / NaN-fix (#54 D6): bump
 *     _gpsFailCount; promote to 'persistent' at persistentFailThreshold.
 *
 * Backoff (#47 R3-F9):
 *   - Fetch errors (network + 5xx + 408/425/429 per #54 D8) increment
 *     _failCount and gate further _callApi calls until a jittered
 *     exponential delay elapses, capped at forcedRefreshMs.
 *   - Successful response resets the counter.
 *
 * Altitude unknown (#47 R3-F10):
 *   - When the device reports altitude=null, altFt is OMITTED from the
 *     request body. Server treats missing altFt as match-all-altitudes.
 */
import { computed, ref } from 'vue';
import apiClient from '@/services/ApiClient';
import { config } from '@/config/config';
import { useContextualStore } from '@/stores/contextualStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { setRuntimePermissionDenied } from '@/composables/useGeolocationPermission';
import { GeolocationPositionProvider } from '@/services/position/GeolocationPositionProvider';
import type {
  PositionProvider,
  PositionProviderKind
} from '@/services/position/PositionProvider';
import { distanceM, initialBearingDeg } from '@/utils/greatCircle';
import { FEET_PER_METER, KT_PER_MS } from '@/utils/units';

const _BACKOFF_BASE_MS = 1000;
// #54 D8 — HTTP 4xx codes whose semantics ARE retry-with-delay. All other
// 4xx codes skip backoff (client-side bugs that don't fix themselves).
const RETRY_WORTHY_4XX = new Set([408, 425, 429]);

// --- Singleton state ----------------------------------------------------
const _isRunning = ref(false);
// The active position source. Swapped only while stopped, and only by an
// explicit user action (#441) — never silently.
// ONE long-lived browser receiver, reused rather than reconstructed. It
// parks a watch it could not release (a backgrounded WebView tears
// navigator.geolocation down) so the next successful arm can clear it —
// and a fresh instance every swap would drop that id on the floor, leaving
// an orphaned watch delivering fixes for the life of the page.
const _realProvider = new GeolocationPositionProvider();
let _provider: PositionProvider = _realProvider;
// The SINGLE source of truth for which source is driving the loop. A
// separate flag kept by the simulator drifted from this one: leaving
// /tracking reverted the provider here but left that flag set, so a real
// GPS fix came back labelled "not your real position". Anything that
// renders provenance reads this.
const _providerKind = ref<PositionProviderKind>(_provider.kind);

function _setProvider(provider: PositionProvider, store?: ReturnType<typeof useContextualStore>): void {
  _provider = provider;
  _providerKind.value = provider.kind;
  store?.setFixesAge(provider.fixesAge);
}
let _forcedTimer: ReturnType<typeof setInterval> | null = null;
let _lastCallPos: { lat: number; lon: number } | null = null;
let _inFlight = false;
let _abortController: AbortController | null = null;
let _callGen = 0;
let _failCount = 0;
let _gpsFailCount = 0;
// Previous position used to compute direction-of-travel when the browser's
// watchPosition callback doesn't include a `heading` field (Chrome
// DevTools Sensors, indoor / low-speed GPS, etc.). Only advanced when we
// actually compute a bearing, so slow cumulative movement still yields
// a direction eventually.
let _prevBearingPos: { lat: number; lon: number } | null = null;
const _BEARING_MIN_DELTA_M = 3;

function _scheduleBackoff(store: ReturnType<typeof useContextualStore>): void {
  _failCount += 1;
  const expDelay = _BACKOFF_BASE_MS * 2 ** Math.min(_failCount - 1, 10);
  const capped = Math.min(expDelay, config.geolocation.forcedRefreshMs);
  const jitter = capped * 0.25 * (Math.random() * 2 - 1);
  const delay = Math.min(config.geolocation.forcedRefreshMs, Math.max(0, capped + jitter));
  store.setNextAllowedAt(Date.now() + delay);
}

function _resetBackoff(store: ReturnType<typeof useContextualStore>): void {
  _failCount = 0;
  store.setNextAllowedAt(0);
}

async function _callApi(
  store: ReturnType<typeof useContextualStore>,
  settings: ReturnType<typeof useSettingsStore>,
  latRaw: number,
  lonRaw: number,
  altMRaw: number | null
): Promise<void> {
  if (_inFlight) return;
  if (Date.now() < store.nextAllowedAt) return;
  const myGen = ++_callGen;
  _inFlight = true;
  let signal: AbortSignal | null = null;
  try {
    _abortController = new AbortController();
    signal = _abortController.signal;
    // R3 finding #4 — NaN altitude guard. `altMRaw != null` alone lets
    // NaN through; NaN * FEET_PER_METER is NaN, JSON.stringify serializes
    // NaN as null, some server validators reject null vs missing.
    const altFt =
      altMRaw != null && Number.isFinite(altMRaw)
        ? altMRaw * FEET_PER_METER
        : undefined;
    const req = {
      lat: latRaw,
      lon: lonRaw,
      altFt,
      lateralNm: settings.contextualLateralNm,
      verticalFt: settings.contextualVerticalFt,
      // Locale from the settings store (user's picked language) so the
      // server returns the plain-English / plain-French summary in the
      // user's language.
      lang: settings.locale
    };
    const resp = await apiClient.getContextualNotams(req, signal);
    if (signal.aborted) return;
    store.setHits(resp.notams, resp.queriedAt);
    // #54 axis separation: successful call clears api_error but does NOT
    // touch gpsState. If GPS is still persistent, the banner stays.
    store.setApiState('ok');
    _lastCallPos = { lat: latRaw, lon: lonRaw };
    _resetBackoff(store);
  } catch (e) {
    if (signal?.aborted) return;
    const msg = e instanceof Error ? e.message : 'contextual fetch failed';
    // #54 axis separation: API failure writes ONLY to apiState.
    store.setApiState('error', msg);
    const httpStatus = (e as { response?: { status?: unknown } } | null)?.response?.status;
    const shouldBackoff =
      typeof httpStatus !== 'number' || // network / non-HTTP failure
      httpStatus >= 500 ||               // 5xx always backoff
      RETRY_WORTHY_4XX.has(httpStatus);  // #54 D8: 408/425/429
    if (shouldBackoff) _scheduleBackoff(store);
  } finally {
    if (_callGen === myGen) {
      _inFlight = false;
      _abortController = null;
    }
  }
}

function _handleGpsFailure(store: ReturnType<typeof useContextualStore>, message: string): void {
  // #54 D6 shared path — POSITION_UNAVAILABLE / TIMEOUT / NaN fix all
  // funnel here.
  _gpsFailCount += 1;
  if (_gpsFailCount >= config.geolocation.persistentFailThreshold) {
    store.setGpsState('persistent', message);
  } else {
    store.setGpsState('transient', message);
  }
  // #56 review F8 — GPS blackout invalidates the bearing anchor. If we
  // held onto the pre-outage position, the very next fix (potentially
  // km away, after the outage clears) would compute a wrong bearing
  // between two non-consecutive positions and briefly invert the plane
  // icon. Reset so the fresh anchor is established when GPS recovers.
  _prevBearingPos = null;
}

function _onPosition(
  store: ReturnType<typeof useContextualStore>,
  settings: ReturnType<typeof useSettingsStore>,
  pos: GeolocationPosition
): void {
  if (!_isRunning.value) return;
  const lat = pos.coords.latitude;
  const lon = pos.coords.longitude;
  const altM = pos.coords.altitude;

  // #54 D6 — NaN lat/lon is a GPS malfunction, not a valid fix. Route
  // through the failure path so the fail counter escalates.
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    _handleGpsFailure(store, 'geolocation error: NaN coordinates');
    return;
  }

  // Successful fix — GPS is healthy.
  _gpsFailCount = 0;
  store.setGpsState('ok');

  const rawHeading = pos.coords.heading;
  const rawSpeed = pos.coords.speed;
  const gpsHeading =
    rawHeading != null && Number.isFinite(rawHeading) ? rawHeading : null;
  const speedKt =
    rawSpeed != null && Number.isFinite(rawSpeed) ? rawSpeed * KT_PER_MS : null;

  // Direction-of-travel fallback: when the GPS callback doesn't include
  // a heading (Chrome DevTools Sensors, indoor chips, low speed), we
  // compute the bearing between the last accepted position and this
  // one. Only advances the anchor once we've computed a bearing so
  // slow cumulative movement still eventually rotates the plane.
  let computedHeading: number | null = null;
  if (_prevBearingPos !== null) {
    const distM = distanceM(_prevBearingPos, { lat, lon });
    if (distM >= _BEARING_MIN_DELTA_M) {
      computedHeading = initialBearingDeg(_prevBearingPos, { lat, lon });
      _prevBearingPos = { lat, lon };
    }
  } else {
    _prevBearingPos = { lat, lon };
  }
  const headingDeg = gpsHeading ?? computedHeading;

  store.setPosition(
    {
      lat,
      lon,
      altFt: altM != null && Number.isFinite(altM) ? altM * FEET_PER_METER : null,
      accuracy: pos.coords.accuracy,
      headingDeg,
      speedKt
    },
    // Prefer the browser's timestamp so age-in-banner reflects the fix
    // moment, not our processing latency (finding #15 from PR #53 R2).
    pos.timestamp
  );

  if (_lastCallPos === null) {
    _callApi(store, settings, lat, lon, altM);
    return;
  }
  if (distanceM(_lastCallPos, { lat, lon }) >= config.geolocation.minDistanceM) {
    _callApi(store, settings, lat, lon, altM);
  }
}

function _onError(
  store: ReturnType<typeof useContextualStore>,
  err: GeolocationPositionError
): void {
  if (!_isRunning.value) return;
  if (err.code === err.PERMISSION_DENIED) {
    // #441 — the permission bridge belongs to the browser's receiver ONLY.
    // A non-geolocation provider fabricates GeolocationPositionError
    // shapes, so an error it raises must never reach the permission
    // singleton: setRuntimePermissionDenied() latches 'denied' for the
    // whole session, which stops the loop from ever arming the receiver
    // again. A simulated source could otherwise lock a pilot out of REAL
    // GPS. Its failures escalate through the shared GPS-failure path.
    if (_provider.kind !== 'geolocation') {
      _handleGpsFailure(store, `position source error: ${err.message}`);
      return;
    }
    // #54: permission axis is external — setRuntimePermissionDenied
    // flows to the permission singleton. The GPS axis becomes 'unknown'
    // because we no longer have any signal from the GPS receiver.
    store.setGpsState('unknown');
    setRuntimePermissionDenied();
    _stopInternal(store);
    return;
  }
  // POSITION_UNAVAILABLE / TIMEOUT flow through the shared handler.
  _handleGpsFailure(store, `geolocation error: ${err.message}`);
}

/**
 * Release the active source, guarded. `_provider` is pluggable now, so
 * `stop()` is third-party code: a throw must never abort the caller's
 * teardown and leave the loop latched as running (frozen aircraft, stale
 * hits, and the forced timer still polling every 30 s).
 */
function _releaseProvider(): void {
  try {
    _provider.stop();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[useContextualNotams] position source stop failed', e);
  }
}

function _startInternal(
  store: ReturnType<typeof useContextualStore>,
  settings: ReturnType<typeof useSettingsStore>
): void {
  if (_isRunning.value) return;
  // R2 finding #13 kept, and widened by #441: the arm sequence is now
  // partly third-party code, so the whole sequence stays inside the
  // guard. Without it a throw from a provider escapes into App.vue's
  // route watcher, and a throw AFTER a successful arm would leave the
  // source running (battery draining) while _isRunning stays false, so
  // _onPosition drops every fix forever with nothing shown to the user.
  try {
    // The provider owns every source-specific precondition and its own
    // failure reporting (unsupported / permission denied / arm throw). A
    // false return means nothing was acquired and no callback will fire.
    if (
      !_provider.start(
        (pos) => _onPosition(store, settings, pos),
        (err) => _onError(store, err)
      )
    ) {
      return;
    }
    _forcedTimer = setInterval(() => {
      if (!_isRunning.value) return;
      if (Date.now() < store.nextAllowedAt) return;
      // R4 finding #12 — don't fire against stale coordinates. When GPS
      // is failing or the fix is old, the request would ask the server
      // about a position the aircraft has already left. On GPS recovery
      // the next posFix immediately triggers _callApi from _onPosition,
      // so we don't lose responsiveness.
      if (store.isStale) return;
      // #527 — a source whose fix never changes gets no periodic refetch:
      // an idle visitor on the approximate map costs one call, not one
      // every 30 s. A dial change still refetches through refresh().
      if (!_provider.fixesAge) return;
      const pos = store.lastPosition;
      if (!pos) return;
      const altM = pos.altFt != null ? pos.altFt / FEET_PER_METER : null;
      _callApi(store, settings, pos.lat, pos.lon, altM);
    }, config.geolocation.forcedRefreshMs);
    _isRunning.value = true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[useContextualNotams] position source arm failed', e);
    _releaseProvider();
    if (_forcedTimer !== null) {
      clearInterval(_forcedTimer);
      _forcedTimer = null;
    }
    _isRunning.value = false;
    // Classify as permission_denied for the user surface — but only for
    // the browser's receiver, per the bridge rule in _onError. Since #449
    // this changes the placeholder the pilot reads, not whether they can
    // reach the view. The console.error above preserves the actual error
    // kind for diagnosis (finding #13).
    if (_provider.kind === 'geolocation') setRuntimePermissionDenied();
  }
}

function _stopInternal(store: ReturnType<typeof useContextualStore>): void {
  _releaseProvider();
  // #441 — every stop reverts to the browser's receiver. Without this a
  // simulated leg would survive leaving /tracking: App.vue re-arms the
  // loop on the next entry with no user action, and the map would show a
  // synthesized position as if it were a real fix. Starting a simulated
  // session is always an explicit action, so resuming one must be too.
  if (_provider.kind !== 'geolocation') {
    _setProvider(_realProvider, store);
  }
  if (_forcedTimer !== null) {
    clearInterval(_forcedTimer);
    _forcedTimer = null;
  }
  if (_abortController !== null) {
    _abortController.abort();
    _abortController = null;
  }
  _callGen++;
  _inFlight = false;
  _isRunning.value = false;
  _lastCallPos = null;
  _failCount = 0;
  _gpsFailCount = 0;
  _prevBearingPos = null;
  // Store-side reset per #54 D5 + D10 — preserves currentHits + prevIds +
  // lastQueriedAt so pulse-diff-across-nav still works, wipes everything
  // else including gpsState/apiState/lastPosition so a stale banner or
  // stale coord never survives across a route change.
  // R4 simplification — every stop() is a full reset. Dropping D5
  // (preserve currentHits + prevIds across nav) eliminates R4 findings
  // #3, #4, #5, #11 by construction.
  store.reset();
}

/**
 * Composable hook. All callers share one underlying loop; start()/stop()
 * are idempotent.
 */
export function useContextualNotams() {
  const store = useContextualStore();
  const settings = useSettingsStore();

  function start(): void {
    _startInternal(store, settings);
  }
  function stop(): void {
    _stopInternal(store);
  }

  /**
   * Best-effort user-initiated refetch. Subject to backoff (#52 Q3 kept).
   * If a call is already in flight, abort it and start fresh so a dial
   * adjust never leaves the bubble + dots mismatched (PR #53 Batch E).
   *
   * #56 review F6 — honor the same isStale guard the forcedTimer added
   * under R4 finding #12. During a GPS blackout store.lastPosition is
   * the pre-blackout coord; firing a request against it would surface
   * proximity NOTAMs for a position the aircraft has already left. On
   * GPS recovery the next _onPosition triggers _callApi directly, so
   * responsiveness isn't lost.
   */
  function refresh(): void {
    if (!_isRunning.value) return;
    if (Date.now() < store.nextAllowedAt) return;
    if (store.isStale) return;
    const pos = store.lastPosition;
    if (!pos) return;
    if (_inFlight && _abortController !== null) {
      _abortController.abort();
      _callGen++;
      _inFlight = false;
      _abortController = null;
    }
    const altM = pos.altFt != null ? pos.altFt / FEET_PER_METER : null;
    _callApi(store, settings, pos.lat, pos.lon, altM);
  }

  /**
   * Swap the position source (#441), then leave the loop stopped.
   *
   * Swapping stops the loop first rather than refusing: the tracking view
   * auto-starts on entry (App.vue watches the route), so by the time a
   * pilot taps "Fly this route" the loop is ALWAYS running — a guard that
   * threw here would make the control dead on its only path. Stopping is
   * also what makes the switch safe: it releases the previous source and
   * resets the store, so no real fix survives into a simulated session or
   * the other way round. The caller starts the new source explicitly,
   * which is the "never a silent switch" rule from the issue.
   */
  function setPositionProvider(provider: PositionProvider): void {
    _stopInternal(store);
    _setProvider(provider, store);
  }

  /** #527 — select the browser's receiver (idempotent; stops the loop). */
  function useRealPositionSource(): void {
    setPositionProvider(_realProvider);
  }

  return {
    start,
    stop,
    refresh,
    setPositionProvider,
    useRealPositionSource,
    isRunning: _isRunning
  };
}

/**
 * Which source is driving the loop — every surface that shows provenance
 * reads this, so the label cannot disagree with what is actually feeding
 * the store. Deliberately NOT a getter for the provider itself: handing
 * out the live instance lets any caller start or stop the source behind
 * the loop's back, which freezes the aircraft glyph with no error shown.
 */
export const positionProviderKind = computed<PositionProviderKind>(() => _providerKind.value);

export function getPositionProviderKind(): PositionProviderKind {
  return _providerKind.value;
}

// --- Test hooks --------------------------------------------------------
export function _resetForTest(): void {
  _releaseProvider();
  _setProvider(_realProvider);
  _realProvider.stop();
  if (_forcedTimer !== null) clearInterval(_forcedTimer);
  if (_abortController !== null) _abortController.abort();
  _forcedTimer = null;
  _abortController = null;
  _inFlight = false;
  _isRunning.value = false;
  _lastCallPos = null;
  _callGen = 0;
  _failCount = 0;
  _gpsFailCount = 0;
  _prevBearingPos = null;
}

export function _getGpsFailCountForTest(): number {
  return _gpsFailCount;
}
